/**
 * Interactions Module - Handles UI event listeners and complex user flows
 */
import { fetchSearchIndex } from './api.js';
import { sanitizeUrlParam, getNewsUrl, escapeHtml } from './utils.js';
import { setupShare } from './share.js';

/**
 * Global interactions setup. Initializes search, theme, navigation, and other UI events.
 * @param {Function} onDateChange - Callback function executed when the user selects a new date from the history selector.
 * @returns {void}
 */
export function setupInteractions(onDateChange) {
  setupSearch();
  setupTheme();
  setupNavigation();
  setupHistorySelector(onDateChange);
  setupReadingMode();
  setupScrollTop();
  setupShare();
}

/**
 * Logic for the search overlay
 */
async function setupSearch() {
  const searchToggle = document.getElementById('searchToggle');
  const searchOverlay = document.getElementById('searchOverlay');
  const searchClose = document.getElementById('searchClose');
  const searchInput = document.getElementById('searchInput');
  const searchResults = document.getElementById('searchResults');
  
  let searchIndex = null;
  let isLoading = false;

  if (searchToggle && searchOverlay && searchInput) {
    searchToggle.addEventListener('click', async (e) => {
      e.preventDefault();
      searchOverlay.classList.add('active');
      setTimeout(() => searchInput.focus(), 300);
      
      if (!searchIndex && !isLoading) {
        isLoading = true;
        try {
          searchIndex = await fetchSearchIndex();
          isLoading = false;
          // Si el usuario ya escribió algo mientras cargaba, disparar la búsqueda
          if (searchInput.value.trim().length >= 2) {
            searchInput.dispatchEvent(new Event('input'));
          }
        } catch(err) {
          console.error('Error loading search index', err);
          isLoading = false;
        }
      }
    });
  }

  if (searchClose && searchOverlay) {
    searchClose.onclick = () => searchOverlay.classList.remove('active');
  }

  if (searchInput && searchResults) {
    searchResults.addEventListener('click', (e) => {
      if (e.target.closest('.search-result-item')) {
        searchOverlay.classList.remove('active');
      }
    });

    searchInput.oninput = (e) => {
      const query = e.target.value.toLowerCase().trim();
      if (!query || query.length < 2) {
        searchResults.innerHTML = '';
        return;
      }
      if (!searchIndex) return;

      const results = searchIndex.filter(item => 
        (item.title && item.title.toLowerCase().includes(query)) || 
        (item.description && item.description.toLowerCase().includes(query)) ||
        (item.tag && item.tag.toLowerCase().includes(query))
      ).slice(0, 7);

      if (results.length === 0) {
        searchResults.innerHTML = `<div class="search-result-empty">No se encontraron resultados para "${escapeHtml(query)}".</div>`;
        return;
      }

      searchResults.innerHTML = results.map(item => {
        const url = item.slug ? getNewsUrl(item.slug, `&date=${item.date}`) : `/historial?fecha=${item.date}`;
        return `
          <a href="${url}" class="search-result-item">
            <div class="search-result-item__main">
              <span class="search-result-item__tag">${escapeHtml(item.tag || 'Noticia')}</span>
              <h4 class="search-result-item__title">${escapeHtml(item.title)}</h4>
              <p class="search-result-item__desc">${item.description ? escapeHtml(item.description).substring(0, 100) + '...' : ''}</p>
            </div>
            <div class="search-result-item__meta"><i class="far fa-calendar-alt"></i> ${escapeHtml(item.date)}</div>
          </a>
        `;
      }).join('');
    };
  }
}

/**
 * Dark/Light theme toggle
 */
function setupTheme() {
  const themeToggle = document.getElementById('themeToggle');
  if (!themeToggle) return;

  const updateIcon = (isDark) => {
    themeToggle.innerHTML = isDark ? '<i class="fas fa-sun"></i>' : '<i class="fas fa-moon"></i>';
  };

  // Initial state is usually handled by a small script in head to prevent flicker, 
  // but we sync here too.
  updateIcon(document.documentElement.classList.contains('dark-theme'));

  themeToggle.onclick = () => {
    const isDark = document.documentElement.classList.toggle('dark-theme');
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
    updateIcon(isDark);
  };
}

/**
 * Mobile navigation menu
 */
function setupNavigation() {
  const menuToggle = document.getElementById('menuToggle');
  const mobileNav = document.getElementById('mobileNav');
  const mobileNavClose = document.getElementById('mobileNavClose');

  if (menuToggle && mobileNav) {
    menuToggle.onclick = () => mobileNav.classList.add('active');
    if (mobileNavClose) {
      mobileNavClose.onclick = () => mobileNav.classList.remove('active');
    }
    mobileNav.querySelectorAll('a').forEach(a => a.onclick = () => mobileNav.classList.remove('active'));
  }
}

/**
 * History date selector
 */
function setupHistorySelector(onDateChange) {
  const historySelect = document.getElementById('historySelect');
  if (!historySelect) return;

  // Assume HISTORIAL_FECHAS is loaded via global script in HTML
  if (typeof window.HISTORIAL_FECHAS !== 'undefined' && window.HISTORIAL_FECHAS.length > 0) {
    let fechasList = Array.isArray(window.HISTORIAL_FECHAS) ? [...window.HISTORIAL_FECHAS] : [window.HISTORIAL_FECHAS];
    fechasList.sort((a, b) => b.localeCompare(a));
    
    const todayISO = new Date().toISOString().split('T')[0];
    const todayLocal = new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0];
    
    fechasList = fechasList.filter(d => d !== todayISO && d !== todayLocal).slice(0, 7);
    
    fechasList.forEach(dateStr => {
      const option = document.createElement('option');
      option.value = dateStr;
      option.textContent = "Edición: " + dateStr;
      historySelect.appendChild(option);
    });

    const urlParams = new URLSearchParams(window.location.search);
    const dateParam = urlParams.get('date');
    if (dateParam && dateParam !== 'today') {
      historySelect.value = dateParam;
    }

    historySelect.addEventListener('change', (e) => {
      const val = e.target.value;
      if (val === 'today') {
        window.location.href = '/';
      } else {
        if (document.getElementById('newsHeroGrid')) {
           onDateChange(val);
        } else {
           const params = new URLSearchParams(window.location.search);
           const isHistorialPage = window.location.pathname.includes('historial');
           const paramName = isHistorialPage ? 'fecha' : 'date';
           params.set(paramName, val);
           window.location.search = params.toString();
        }
      }
    });
  }
}

/**
 * Focus/Reading mode
 */
function setupReadingMode() {
  const readingBtn = document.getElementById('readingModeBtn');
  if (!readingBtn) return;

  let readingMode = false;
  readingBtn.onclick = () => {
    readingMode = !readingMode;
    document.body.classList.toggle('focus-mode-active');
    
    readingBtn.innerHTML = readingMode ?
      '<i class="fas fa-compress-alt"></i> Vista Normal' :
      '<i class="fas fa-book-reader"></i> Lectura Rápida';
    readingBtn.style.background = readingMode ? 'var(--brand-navy)' : '';
    readingBtn.style.color = readingMode ? '#fff' : '';
  };
}

/**
 * Back to top button
 */
function setupScrollTop() {
  const scrollTop = document.getElementById('scrollTop');
  if (!scrollTop) return;

  window.addEventListener('scroll', () => {
    scrollTop.classList.toggle('visible', window.scrollY > 500);
  });
  scrollTop.onclick = () => window.scrollTo({ top: 0, behavior: 'smooth' });
}

